import { useState, useRef } from 'react';

// Utils
import api from '../../js/api';

// Hooks
import useCustomerAccessToken from '@arctheme-hooks/useCustomerAccessToken';

type CustomerInput = {
    customer: {
        password: string;
    };
    customerAccessToken: string;
};

const useCustomerUpdatePassword = (formElement: HTMLFormElement) => {
    // States
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState(false);

    // Ref
    const formRef = useRef(null);

    // Hooks
    const { updateAccessToken } = useCustomerAccessToken();

    const updateCustomerPassword = async (password: string, passwordConfirm: string, accessToken: string) => {
        setLoading(true);
        setError('');
        setSuccess(false);

        const CUSTOMER_UPDATE_MUTATION = `
      mutation customerUpdate($customer: CustomerUpdateInput!, $customerAccessToken: String!) {
        customerUpdate(customer: $customer, customerAccessToken: $customerAccessToken) {
            customer {
              id
            }
            customerAccessToken {
              accessToken
              expiresAt
            }
            customerUserErrors {
              code
              field
              message
            }
            userErrors {
              field
              message
            }
        }
      }
    `;

        // Create input fields
        const customerInput: CustomerInput = {
            customer: {
                password: password
            },
            customerAccessToken: accessToken
        };

        if (!password || !passwordConfirm) {
            setError('Password and Password confirmation are required');
            setLoading(false);

            return;
        }

        if (password !== passwordConfirm) {
            setError('Password and Password confirmation must match');
            setLoading(false);

            return;
        }

        try {
            const res = await fetch('api/2024-07/graphql.json', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'X-Shopify-Storefront-Access-Token': api.getStorefrontToken()
                },
                body: JSON.stringify({
                    query: CUSTOMER_UPDATE_MUTATION,
                    variables: customerInput
                })
            });

            if (!res.ok) {
                throw new Error('Failed to update password. Please try another time.');
            }

            const { data, errors } = await res.json();

            if (errors) throw new Error(errors[0].message);

            const { userErrors } = data.customerUpdate;
            if (userErrors.length > 0) {
                let errorMessage = '';

                // Combine all error messages & Get fields with error
                userErrors.forEach((error) => {
                    errorMessage += `${error.message}\n`;
                });

                throw new Error(errorMessage);
            }

            const { customerUserErrors } = data.customerUpdate;
            if (customerUserErrors.length > 0) {
                let errorMessage = '';

                // Combine all error messages & Get fields with error
                customerUserErrors.forEach((error) => {
                    errorMessage += `${error.message}\n`;
                });

                throw new Error(errorMessage);
            }

            // Set the new access token since the previous access token become invalid
            const newAccessToken = data.customerUpdate.customerAccessToken.accessToken;
            updateAccessToken(newAccessToken);

            setSuccess(true);

            // Programmatically logout customer
            fetch('/account/logout');

            // Submit form to redirect to login page
            formElement.submit();
        } catch (err) {
            setError((err as Error).message);
        } finally {
            setLoading(false);
        }
    };

    return {
        loading,
        error,
        success,

        updateCustomerPassword
    };
};

export default useCustomerUpdatePassword;